import { TableSettingsTabType, getWebPath, isFormulaType } from '@doss/common-all'
import { MoreVertRounded } from '@mui/icons-material'
import { MenuItem, Stack, Typography } from '@mui/material'
import { Settings } from 'lucide-react'
import { useEffect, useState } from 'react'
import { RxCopy, RxInfoCircled, RxTrash } from 'react-icons/rx'
import { useNavigate } from 'react-router'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { apolloClient } from '../../common/apollo-client'
import {
  BreadcrumbObjectType,
  useDuplicateTableRowMutation,
} from '../../common/generated/apollo-operations'
import { DossSignalType, useDossSignals } from '../../common/hooks/useDossSignals'
import { clearAllTableRowsFromCache } from '../../common/utils/table'
import { useDossSnackbar } from '../shared/DossSnackbar'
import { Menu } from '../ui/atoms/menu/Menu'
import { DeleteRecordDialog } from './DeleteRecordDialog'
import { RecordDuplicateSaveAndDuplicateDialog } from './RecordDuplicateSaveAndDuplicateDialog'
import { RecordPropertiesDialog } from './RecordPropertiesDialog'
import { RecordDetailTableRow } from './types'

interface RecordDetailsMenuProps {
  tableRow: RecordDetailTableRow
}

const enum RecordDetailsMenuItems {
  PROPERTIES = 'properties',
  SAVE_AND_DUPLICATE = 'save_and_duplicate',
  TABLE_SETTINGS = 'table_settings',
  DELETE = 'delete',
}

/**
 * Dropdown kebab menu for table records
 */
export function RecordDetailsMenu({ tableRow }: RecordDetailsMenuProps) {
  const navigate = useNavigate()
  const [openMenu, setOpenMenu] = useState(false)
  const { isAdmin, isInternal, permissionsManager } = useGlobalContext()
  const [openDialog, setOpenDialog] = useState<RecordDetailsMenuItems | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [duplicateTableRow] = useDuplicateTableRowMutation({
    onCompleted() {
      clearAllTableRowsFromCache(apolloClient.cache)
    },
  })
  const snackbar = useDossSnackbar()

  const { t } = useTypedTranslation()

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenMenu(true)
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setOpenMenu(false)
    setAnchorEl(null)
  }

  const handleOpenRecordMenuItem = (dialogTarget: RecordDetailsMenuItems) => () => {
    if (dialogTarget === RecordDetailsMenuItems.TABLE_SETTINGS) {
      navigate(
        getWebPath({
          objectType: BreadcrumbObjectType.TABLE,
          objectId: tableRow.table.id,
          settings: true,
          settingsParams: { tab: TableSettingsTabType.FIELDS },
        }),
      )
    }
    setOpenMenu(false)
    setOpenDialog(dialogTarget)
  }

  const { subscribe, unsubscribe } = useDossSignals()
  const [isDirty, setIsDirty] = useState(false)
  useEffect(() => {
    const subscriptionId = subscribe(
      DossSignalType.RECORD_FORM_DIRTY_STATE_CHANGE,
      async (isDirty) => setIsDirty(isDirty),
    )
    return () => {
      unsubscribe(subscriptionId)
    }
  }, [subscribe, unsubscribe])

  const canDuplicateRecord = permissionsManager.canAddRowToTable(
    tableRow.table.id,
    tableRow.table.columns
      .filter(
        (column) => !column.isNullable && !column.indexAutogenerate && !isFormulaType(column.type),
      )
      .map((column) => column.id),
  )
  const canDeleteRecord = permissionsManager.canDeleteRowFromTable(tableRow.table.id)

  const handleDuplicateRecord = async (tableRowId: string) => {
    if (canDuplicateRecord) {
      if (isDirty) {
        handleOpenRecordMenuItem(RecordDetailsMenuItems.SAVE_AND_DUPLICATE)()
        return
      }
      const duplicateMutation = duplicateTableRow({
        variables: {
          tableRowId,
        },
      })
      await duplicateMutation
        .then(({ data }) => {
          snackbar.showSuccess(t('record.duplicate_success'))

          if (data) {
            navigate(
              getWebPath({
                objectType: BreadcrumbObjectType.TABLE_ROW,
                objectId: data.duplicateTableRow.id,
              }),
            )
          }
        })
        .catch((error) => snackbar.showError(error.message))
    }
  }

  return (
    <>
      <button
        className="flex cursor-pointer items-center justify-center rounded p-2 hover:bg-gray-2"
        onClick={handleOpenMenu}
      >
        <MoreVertRounded fontSize="small" />
      </button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={handleCloseMenu}
        open={openMenu}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleOpenRecordMenuItem(RecordDetailsMenuItems.PROPERTIES)}>
          <Stack alignItems="center" direction="row" padding={0.75} spacing={1}>
            <RxInfoCircled size={16} />
            <Typography>{t('record.properties.properties')}</Typography>
          </Stack>
        </MenuItem>
        {canDuplicateRecord && (
          <MenuItem onClick={() => handleDuplicateRecord(tableRow.id)}>
            <Stack alignItems="center" direction="row" padding={0.75} spacing={1}>
              <RxCopy size={16} />
              <Typography>{t('record.duplicate_record')}</Typography>
            </Stack>
          </MenuItem>
        )}
        {(isAdmin || isInternal) && (
          <MenuItem onClick={handleOpenRecordMenuItem(RecordDetailsMenuItems.TABLE_SETTINGS)}>
            <Stack alignItems="center" direction="row" padding={0.75} spacing={1}>
              <Settings height={16} width={16} />
              <Typography>
                {t('common.configure')} {tableRow.table.name || t('table.parent_table')}
              </Typography>
            </Stack>
          </MenuItem>
        )}
        {canDeleteRecord && (
          <MenuItem onClick={handleOpenRecordMenuItem(RecordDetailsMenuItems.DELETE)}>
            <Stack alignItems="center" direction="row" padding={0.75} spacing={1}>
              <RxTrash size={16} />
              <Typography>{t('record.delete_record')}</Typography>
            </Stack>
          </MenuItem>
        )}
      </Menu>
      <DeleteRecordDialog
        handleCloseDeleteRecord={() => setOpenDialog(null)}
        isChildTableRecord={false}
        openDialog={openDialog === RecordDetailsMenuItems.DELETE}
        tableId={tableRow.table.id}
        tableRow={tableRow}
      />
      <RecordPropertiesDialog
        handleCloseRecordProperties={() => setOpenDialog(null)}
        openDialog={openDialog === RecordDetailsMenuItems.PROPERTIES}
        tableRow={tableRow}
      />
      <RecordDuplicateSaveAndDuplicateDialog
        handleCloseSaveAndDuplicate={() => setOpenDialog(null)}
        handleDuplicateRecord={handleDuplicateRecord}
        openDialog={openDialog === RecordDetailsMenuItems.SAVE_AND_DUPLICATE}
      />
    </>
  )
}
